import { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  Button,
  Card,
  Description,
  FieldError,
  Input,
  Label,
  TextField,
} from "@heroui/react";
import { api, AppUser, MealSlot, NutritionProfile, nutritionProfileOf, SlotException } from "../api/client";
import { SlotDefaultsEditor } from "../components/SlotDefaultsEditor";
import { SlotExceptionsEditor } from "../components/SlotExceptionsEditor";
import { NutritionProfileEditor } from "../components/NutritionProfileEditor";
import { useAuth } from "../auth";
import { usePendingPrepCount } from "../hooks/usePendingPrepCount";

export function Profile() {
  const { user, refresh, logout } = useAuth();
  if (!user) return null;
  return (
    <div className="mx-auto max-w-xl">
      <h1 className="mb-4 text-2xl font-bold">Edita el perfil</h1>
      {/* Accessos ràpids: només visibles al mòbil (CSS), on la barra de dalt no hi és */}
      <ProfileQuickLinks isAdmin={user.role === "admin"} onLogout={logout} />
      <ProfileForm user={user} onSaved={refresh} />
    </div>
  );
}

interface ProfileQuickLinksProps {
  isAdmin: boolean;
  onLogout: () => Promise<void>;
}

function ProfileQuickLinks({ isAdmin, onLogout }: ProfileQuickLinksProps) {
  const prepCount = usePendingPrepCount();
  return (
    <div className="profile-quicklinks">
      <NavLink to="/prep" className="profile-quicklink">
        <span className="ic" aria-hidden>🔔</span>
        Preparacions
        {prepCount > 0 && (
          <span className="profile-quicklink-badge">{prepCount > 9 ? "9+" : prepCount}</span>
        )}
      </NavLink>
      <NavLink to="/millores" className="profile-quicklink">
        <span className="ic" aria-hidden>✨</span>
        Millores
      </NavLink>
      {isAdmin && (
        <NavLink to="/ingredients" className="profile-quicklink">
          <span className="ic" aria-hidden>🧂</span>
          Ingredients
        </NavLink>
      )}
      {isAdmin && (
        <NavLink to="/usuaris" className="profile-quicklink">
          <span className="ic" aria-hidden>👥</span>
          Gestiona usuaris
        </NavLink>
      )}
      <button type="button" className="profile-quicklink danger" onClick={onLogout}>
        <span className="ic" aria-hidden>🚪</span>
        Sortir
      </button>
    </div>
  );
}

interface ProfileFormProps {
  user: AppUser;
  onSaved: () => Promise<void>;
}

function ProfileForm({ user, onSaved }: ProfileFormProps) {
  const [name, setName] = useState(user.name);
  const [emoji, setEmoji] = useState(user.avatarEmoji);
  const [color, setColor] = useState(user.avatarColor);
  const [prefs, setPrefs] = useState(stringifyOrEmpty(user.preferences));
  const [intol, setIntol] = useState(stringifyOrEmpty(user.intolerances));
  const [slotDefaults, setSlotDefaults] = useState<MealSlot[]>(user.slotDefaults ?? []);
  const [slotExceptions, setSlotExceptions] = useState<SlotException[]>(user.slotExceptions ?? []);
  const [nutritionProfile, setNutritionProfile] = useState<NutritionProfile>(nutritionProfileOf(user));
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [pwdError, setPwdError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const passwordInvalid = password.length > 0 && password.length < 6;

  async function handleSave() {
    if (passwordInvalid) {
      setPwdError("Mínim 6 caràcters");
      return;
    }
    setBusy(true);
    setPwdError(null);
    setOk(false);
    try {
      const payload: Record<string, unknown> = {
        name,
        avatarEmoji: emoji,
        avatarColor: color,
        preferences: prefs,
        intolerances: intol,
        slotDefaults,
        slotExceptions,
        ...nutritionProfile,
      };
      if (password) payload.password = password;
      await api.patch<AppUser>(`/users/me`, payload);
      setPassword("");
      await onSaved();
      setOk(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <Card.Content className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <div
            className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-3xl"
            style={{ background: color }}
            aria-hidden
          >
            {emoji}
          </div>
          <div className="flex-1">
            <div className="text-base font-semibold">{user.email}</div>
            <div className="text-xs text-muted">
              Rol: {user.role === "admin" ? "Administrador" : "Membre"}
            </div>
          </div>
        </div>

        <TextField value={name} onChange={setName}>
          <Label>Nom</Label>
          <Input />
        </TextField>

        <div className="flex flex-wrap gap-3">
          <TextField className="w-24" value={emoji} onChange={setEmoji}>
            <Label>Emoji</Label>
            <Input className="text-center" />
          </TextField>
          <div className="flex flex-col gap-1">
            <span className="text-sm">Color</span>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-10 w-16 cursor-pointer rounded border border-default-200 bg-transparent"
              aria-label="Color avatar"
            />
          </div>
        </div>

        <TextField value={prefs} onChange={setPrefs}>
          <Label>Preferències</Label>
          <Input />
          <Description>Text lliure</Description>
        </TextField>

        <TextField value={intol} onChange={setIntol}>
          <Label>Intoleràncies / al·lèrgies</Label>
          <Input />
        </TextField>

        <SlotDefaultsEditor
          value={slotDefaults}
          onChange={setSlotDefaults}
        />

        <SlotExceptionsEditor
          slotDefaults={slotDefaults}
          value={slotExceptions}
          onChange={setSlotExceptions}
        />

        <NutritionProfileEditor value={nutritionProfile} onChange={setNutritionProfile} />

        <TextField
          isInvalid={passwordInvalid || !!pwdError}
          type="password"
          value={password}
          onChange={setPassword}
        >
          <Label>Nova contrasenya</Label>
          <Input placeholder="Deixa-ho buit per no canviar-la" />
          {passwordInvalid ? (
            <FieldError>Mínim 6 caràcters</FieldError>
          ) : pwdError ? (
            <FieldError>{pwdError}</FieldError>
          ) : (
            <Description>Opcional. Mínim 6 caràcters si la canvies.</Description>
          )}
        </TextField>
      </Card.Content>
      <Card.Footer className="flex items-center gap-3">
        <Button isPending={busy} onPress={handleSave}>
          Desa
        </Button>
        {ok && <span className="text-sm text-success">Canvis desats.</span>}
      </Card.Footer>
    </Card>
  );
}

function stringifyOrEmpty(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
}
